import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { formatRating } from '../../utils/formatters';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const FeaturedContainer = styled.div`
  position: relative;
  height: 100vh;
  max-height: 1000px;
  min-height: 700px;
  width: 100%;
  overflow: hidden;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    height: 90vh;
    min-height: 600px;
  }
`;

const BackdropSlider = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center 20%;
  opacity: ${props => props.active ? 1 : 0};
  transition: opacity 0.8s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      rgba(0,0,0,0.9) 0%, 
      rgba(0,0,0,0.7) 30%, 
      rgba(0,0,0,0.4) 60%, 
      rgba(0,0,0,0.2) 100%),
      linear-gradient(0deg,
      rgba(0,0,0,0.9) 0%,
      rgba(0,0,0,0.5) 20%,
      rgba(0,0,0,0) 40%);
  }
`;

const ContentWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 60px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  animation: ${fadeIn} 0.5s ease-out;
  
  @media (max-width: 1024px) {
    padding: 40px;
  }
  
  @media (max-width: 768px) {
    padding: 30px 20px 60px; /* Added bottom padding for dots */
  }
`;

const ContentBox = styled.div`
  max-width: 600px;
  animation: ${slideUp} 0.5s ease-out;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 64px;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.1;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 1024px) {
    font-size: 48px;
  }
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const GenreTags = styled.div`
  display: flex;
  margin-bottom: 15px;
  flex-wrap: wrap;
  
  span {
    background-color: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(5px);
    padding: 4px 12px;
    border-radius: 4px;
    margin-right: 10px;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.8);
  flex-wrap: wrap;
  
  & > * {
    margin-right: 15px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
  }
  
  .dot-separator:not(:last-child)::after {
    content: "•";
    margin-left: 15px;
    opacity: 0.7;
  }
  
  .rating {
    .star {
      color: #ffc107;
      margin-right: 5px;
    }
  }
`;

const Description = styled.p`
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  max-width: 90%;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  
  @media (max-width: 768px) {
    -webkit-line-clamp: 2;
    font-size: 14px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const WatchButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
    transform: scale(1.05);
  }
  
  svg {
    margin-right: 10px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const InfoButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(5px);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
  
  svg {
    margin-right: 10px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 3;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--primary-light)' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-light)' : 'rgba(255, 255, 255, 0.8)'};
    transform: scale(1.2);
  }
  
  @media (max-width: 768px) {
    width: 8px;
    height: 8px;
  }
`;

//truncate
const truncateText = (text, maxLength = 150) => {
  if (!text) return 'No description available.';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

//preloader function
const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = reject;
  });
};

const Featured = ({ items = [] }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeItems, setActiveItems] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const featuredRef = useRef(null);
  
  //5 
  useEffect(() => {
    if (items && items.length > 0) {
      const limitedItems = items.slice(0, 5);
      setActiveItems(limitedItems);
      
      //all images
      const preloadAll = async () => {
        setImagesLoaded(false);
        try {
          const imagePromises = limitedItems.map(item => 
            preloadImage(item.coverImage || item.image)
          );
          await Promise.all(imagePromises);
          setImagesLoaded(true);
        } catch (error) {
          console.error('Failed to preload images:', error);
          setImagesLoaded(true); // Continue anyway
        }
      };
      
      preloadAll();
    }
  }, [items]);
  
  // autoslide every 6 seconds
  useEffect(() => {
    if (activeItems.length <= 1 || !imagesLoaded) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => 
        prevIndex === activeItems.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000);
    
    return () => clearInterval(interval);
  }, [activeItems, imagesLoaded]);
  
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleNextSlide();
    }
    
    if (isRightSwipe) {
      handlePrevSlide();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  const handlePrevSlide = () => {
    setCurrentIndex(prevIndex => 
      prevIndex === 0 ? activeItems.length - 1 : prevIndex - 1
    );
  };
  
  const handleNextSlide = () => {
    setCurrentIndex(prevIndex => 
      prevIndex === activeItems.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  if (!activeItems || activeItems.length === 0) return null;
  
  const item = activeItems[currentIndex];
  
  const handleWatchClick = () => {
    const type = item.type || (item.seasons ? 'show' : 'movie');
    navigate(`/${type === 'movie' ? 'movie' : 'show'}/${item._id}`);
  };
  
  const handleInfoClick = () => {
    const type = item.type || (item.seasons ? 'show' : 'movie');
    navigate(`/${type === 'movie' ? 'movie' : 'show'}/${item._id}`);
  };
  
  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };
  
  const contentType = item.type === 'show' || (!item.type && item.seasons) ? 'TV Series' : 'Movie';
  const year = contentType === 'Movie' ? item.releaseYear : item.startYear;
  const rating = formatRating(item.rating);
  const truncatedDescription = truncateText(item.description, 180);
  
  return (
    <FeaturedContainer 
      ref={featuredRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <BackdropSlider>
        {activeItems.map((featuredItem, index) => (
          <Backdrop 
            key={featuredItem._id}
            image={featuredItem.coverImage || featuredItem.image} 
            active={index === currentIndex}
          />
        ))}
      </BackdropSlider>
      
      <ContentWrapper>
        <ContentBox>
          <Title>{item.title}</Title>
          
          <GenreTags>
            {item.tags && item.tags.slice(0, 3).map((tag, index) => (
              <span key={index}>{tag}</span>
            ))}
          </GenreTags>
          
          <MetaInfo>
            <span className="dot-separator">{contentType}</span>
            {year && <span className="dot-separator">{year}</span>}
            {rating !== 'N/A' && (
              <span className="rating">
                <span className="star">★</span> {rating}
              </span>
            )}
          </MetaInfo>
          
          <Description>
            {truncatedDescription}
          </Description>
          
          <Actions>
            <WatchButton onClick={handleWatchClick}>
              <FontAwesomeIcon icon={faPlay} /> Watch Now
            </WatchButton>
            <InfoButton onClick={handleInfoClick}>
              <FontAwesomeIcon icon={faInfoCircle} /> More Info
            </InfoButton>
          </Actions>
        </ContentBox>
      </ContentWrapper>
      
      {activeItems.length > 1 && (
        <DotsContainer>
          {activeItems.map((_, index) => (
            <Dot 
              key={index} 
              active={index === currentIndex}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </DotsContainer>
      )}
    </FeaturedContainer>
  );
};

export default Featured;